import React, {ReactElement, useEffect, useRef, useState} from 'react';
import {Controller, FormProvider, useForm} from 'react-hook-form';
import {Typography, Button} from '@customink/pigment-react';
import {TextField} from '@mui/material';
import {ContactDetails} from 'models/contactDetails';
import {useHistory} from 'react-router-dom';
import {StoreDetails} from 'models/storeDetails';
import services from '../../api/remoteServices';
import {
  useGetContactDetailsQuery,
  useLazyGetUserAccountQuery,
  useUpdateContactDetailsMutation
} from '../../api/storesApi';
import Label from '../Label';
import AlertConfirmation from './AlertConfirmation';
import SettingDialog from './Dialog';

type ContactDetailsFormProps = {
  storeSlug: string;
  storeDetails: StoreDetails;
};

const ContactDetailsForm: React.FC<ContactDetailsFormProps> = ({
  storeSlug,
  storeDetails: {capabilities: storeCapabilities, name: storeName}
}: ContactDetailsFormProps): ReactElement => {
  const defaultValues: ContactDetails = {
    organizerFirstName: '',
    organizerLastName: '',
    organizerPhoneNumber: '',
    organizerEmail: ''
  };

  const [
    triggerUpdateContactDetailsMutation,
    {
      isError: isUpdateContactDetailsError,
      isLoading: isUpdateContactDetailsLoading
    }
  ] = useUpdateContactDetailsMutation();

  const [triggerGetUserAccountQuery, {isError: isUserAccountError}] =
    useLazyGetUserAccountQuery();

  const history = useHistory();

  const formMethods = useForm<ContactDetails>({
    defaultValues
  });
  const {
    control,
    reset: resetForm,
    formState: {isDirty: isFormDirty, isSubmitted: isFormSubmitted},
    getValues: getFormValues
  } = formMethods;

  const unblockRef = useRef(null);

  const [showGeneralError, setShowGeneralError] = useState(false);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [currentPath, setCurrentPath] = useState('');

  const {data: contactDetailsData} = useGetContactDetailsQuery({
    storeSlug
  });

  const patchContactDetails = (data: ContactDetails) => {
    triggerUpdateContactDetailsMutation({
      storeSlug,
      organizerFirstName: data.organizerFirstName,
      organizerLastName: data.organizerLastName,
      organizerPhoneNumber: data.organizerPhoneNumber,
      organizerEmail: data.organizerEmail
    }).then((response: any) => {
      if (response?.data) {
        if (
          response.data.organizer_email !== contactDetailsData.organizerEmail
        ) {
          window.location.href = services.homePage;
        }
        setShowSuccessAlert(!isUpdateContactDetailsError);
      }
    });
  };

  /* 
    Form handlers
  */

  const handleSubmit = async (data: ContactDetails) => {
    if (data.organizerEmail !== contactDetailsData.organizerEmail) {
      const userAccount = await triggerGetUserAccountQuery({
        email: data.organizerEmail
      });

      if (userAccount.data?.id) {
        setShowConfirmDialog(true);
      }
    } else {
      patchContactDetails(data);
    }
  };

  const handleLeaveDialogConfirm = () => {
    if (unblockRef) {
      unblockRef.current();
    }
    setShowLeaveDialog(false);
    history.push(currentPath);
  };

  const handleTransferOwnershipModalConfirm = () => {
    patchContactDetails(getFormValues());
    setShowConfirmDialog(false);
  };

  const handleTransferOwnershipModalCancel = () => {
    setShowConfirmDialog(false);
    resetForm();
  };

  const handleCloseAlert = () => setShowSuccessAlert(false);

  /* 
    useEffect hooks
  */

  // Set form default values when settings load
  useEffect(() => {
    if (contactDetailsData) {
      const newValues = {
        ...getFormValues(),
        organizerFirstName: contactDetailsData.organizerFirstName,
        organizerLastName: contactDetailsData.organizerLastName,
        organizerPhoneNumber: contactDetailsData.organizerPhoneNumber,
        organizerEmail: contactDetailsData.organizerEmail
      };
      resetForm({...newValues});
    }
  }, [contactDetailsData]);

  useEffect(() => {
    unblockRef.current = history.block((location: typeof history) => {
      if (isFormDirty && !isFormSubmitted) {
        setCurrentPath(location.pathname);
        setShowLeaveDialog(true);
        return false;
      }
      return true;
    });
    return () => unblockRef.current && unblockRef.current();
  }, [formMethods.formState, isFormDirty]);

  useEffect(() => {
    setShowGeneralError(isUpdateContactDetailsError || isUserAccountError);
  }, [isUpdateContactDetailsError, isUserAccountError]);

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={formMethods.handleSubmit(handleSubmit)} className="form">
        {showGeneralError && (
          <Typography variant="subHeading" className="generalError">
            {isUserAccountError
              ? 'The email does not match any valid accounts.'
              : 'Something went wrong. Please try again later.'}
          </Typography>
        )}
        <div className="form-field">
          <div className="tab-subheading">
            <Typography variant="subHeading">Contact Details</Typography>
          </div>
          {storeCapabilities?.includes('configureContactDetails') ? (
            <>
              <div className="form-field-split">
                <div className="split-input">
                  <Label required>Organizer First Name</Label>
                  <Controller
                    render={({field}) => (
                      <TextField
                        value={field.value}
                        onChange={field.onChange}
                        inputRef={field.ref}
                        required
                      />
                    )}
                    name="organizerFirstName"
                    control={control}
                  />
                </div>
                <div className="split-input">
                  <Label required>Organizer Last Name</Label>
                  <Controller
                    render={({field}) => (
                      <TextField
                        value={field.value}
                        onChange={field.onChange}
                        inputRef={field.ref}
                        required
                      />
                    )}
                    name="organizerLastName"
                    control={control}
                  />
                </div>
              </div>
              <div className="form-field-split">
                <div className="split-input">
                  <Label required>Organizer Phone Number</Label>
                  <Controller
                    render={({field}) => (
                      <TextField
                        value={field.value}
                        onChange={field.onChange}
                        inputRef={field.ref}
                        required
                      />
                    )}
                    name="organizerPhoneNumber"
                    control={control}
                  />
                </div>
                <div className="split-input">
                  <Label required>Organizer Email Address</Label>
                  <Controller
                    render={({field}) => (
                      <TextField
                        value={field.value}
                        onChange={field.onChange}
                        inputRef={field.ref}
                        type="email"
                        required
                      />
                    )}
                    name="organizerEmail"
                    control={control}
                  />
                </div>
              </div>
            </>
          ) : null}
        </div>
        <SettingDialog
          open={showConfirmDialog}
          title={`Are you sure you want to transfer the ownership of ${storeName}?`}
          alertContent={`After confirmation ${getFormValues(
            'organizerEmail'
          )} will be the new organizer of this offering.`}
          descriptionContent="Completing this action will remove your ability to manage this offering and you will be redirected to customink.com"
          confirmText="Transfer ownership"
          closeText="Cancel"
          handleConfirm={handleTransferOwnershipModalConfirm}
          handleClose={handleTransferOwnershipModalCancel}
        />
        <div className="settingsActions" style={{paddingBottom: 0}}>
          <Button
            className="cancel-button"
            aria-label="reset"
            variant="link"
            disabled={!isFormDirty || isUpdateContactDetailsLoading}
            onClick={() => {
              setShowGeneralError(false);
              resetForm();
            }}>
            Cancel
          </Button>
          <Button
            className="submit-button"
            type="submit"
            disabled={!isFormDirty || isUpdateContactDetailsLoading}>
            Save
          </Button>
        </div>
      </form>
      <SettingDialog
        open={showLeaveDialog}
        title="Are you sure you want to leave without saving?"
        alertContent="You have unsaved changes. If you continue without saving, your recent changes will be lost."
        closeText="Go Back"
        confirmText="Continue Without Saving"
        handleConfirm={handleLeaveDialogConfirm}
        handleClose={() => setShowLeaveDialog(false)}
      />
      <AlertConfirmation
        showSuccessAlert={showSuccessAlert}
        onCloseAlert={handleCloseAlert}
        groupOrderAlert={{
          message: 'Your contact details have been saved.'
        }}
      />
    </FormProvider>
  );
};

export default ContactDetailsForm;
